import React, { useState, useEffect } from 'react';
import { Loader2, FileText, Mail, Phone, Building2, Check, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';

import { API_URL, BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const AdminQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/quotes`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setQuotes(data);
      }
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateQuoteStatus = async (quoteId, status) => {
    try {
      const response = await fetch(`${API}/admin/quotes/${quoteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        console.log('Success:', 'Statut mis à jour');
        fetchQuotes();
        if (selectedQuote?.quote_id === quoteId) {
          setSelectedQuote(prev => ({ ...prev, status }));
        }
      }
    } catch (error) {
      console.log('Error:', 'Erreur');
    }
  };

  const handleDelete = async (quoteId) => {
    try {
      const response = await fetch(`${API}/admin/quotes/${quoteId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        console.log('Success:', 'Devis supprimé');
        setQuotes(prev => prev.filter(q => q.quote_id !== quoteId));
        if (selectedQuote?.quote_id === quoteId) {
          setSelectedQuote(null);
        }
        setDeleteConfirm(null);
      } else {
        console.log('Error:', 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.log('Error:', 'Erreur de connexion');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-500/20 text-blue-400',
      pending: 'bg-yellow-500/20 text-yellow-400',
      contacted: 'bg-purple-500/20 text-purple-400',
      quoted: 'bg-green-500/20 text-green-400',
      closed: 'bg-gray-500/20 text-fgs-text-muted',
    };
    const labels = {
      new: 'Nouveau',
      pending: 'En attente',
      contacted: 'Contacté',
      quoted: 'Devis envoyé',
      closed: 'Clôturé',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.new}`}>
        {labels[status] || 'Nouveau'}
      </span>
    );
  };

  const getProjectType = (type) => {
    const types = {
      alarm: 'Alarme',
      video: 'Vidéoprotection',
      ssi: 'SSI / Incendie',
      automation: 'Domotique',
      pro: 'Pro / Grade 2',
      fire: 'Incendie',
      grade3: 'Grade 3'
    };
    return types[type] || type || 'Non spécifié';
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-bold text-white">Demandes de devis</h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Quotes List */}
        <div className="lg:col-span-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-fgs-primary" />
            </div>
          ) : quotes.length === 0 ? (
            <div className="bg-fgs-surface border border-fgs-border rounded-xl p-12 text-center">
              <FileText className="w-16 h-16 text-fgs-text-muted mx-auto mb-4" />
              <p className="text-fgs-text-muted">Aucune demande de devis</p>
            </div>
          ) : (
            <div className="space-y-3">
              {quotes.map(quote => (
                <div
                  key={quote.quote_id}
                  className={`bg-fgs-surface border rounded-xl p-4 transition-colors ${
                    selectedQuote?.quote_id === quote.quote_id 
                      ? 'border-fgs-primary' 
                      : 'border-fgs-border hover:border-fgs-primary/50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div 
                      className="flex items-center gap-2 flex-1 cursor-pointer"
                      onClick={() => setSelectedQuote(quote)}
                    >
                      <span className="text-white font-medium">{quote.name}</span>
                      {quote.company && (
                        <span className="text-fgs-text-muted text-xs">({quote.company})</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(quote.status)}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteConfirm(quote.quote_id);
                        }}
                        className="p-1.5 hover:bg-red-500/20 rounded text-fgs-text-muted hover:text-red-400 transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div 
                    className="flex items-center justify-between text-sm cursor-pointer"
                    onClick={() => setSelectedQuote(quote)}
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-fgs-text-muted">{quote.email}</span>
                      {(quote.category || quote.project_type) && (
                        <span className="px-2 py-0.5 bg-fgs-primary/20 text-fgs-primary rounded text-xs">
                          {getProjectType(quote.category || quote.project_type)}
                        </span>
                      )}
                    </div>
                    <span className="text-fgs-text-muted text-xs">
                      {new Date(quote.created_at).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                  
                  {/* Delete confirmation */}
                  {deleteConfirm === quote.quote_id && (
                    <div className="mt-3 pt-3 border-t border-fgs-border">
                      <div className="flex items-center gap-2 text-yellow-400 text-sm mb-2">
                        <AlertTriangle className="w-4 h-4" />
                        <span>Supprimer ce devis ?</span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(quote.quote_id)}
                          className="bg-red-500 hover:bg-red-600"
                        >
                          Supprimer
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDeleteConfirm(null)}
                          className="border-fgs-border text-fgs-text-muted"
                        >
                          Annuler
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quote Details */}
        <div className="lg:col-span-1">
          {selectedQuote ? (
            <div className="bg-fgs-surface border border-fgs-border rounded-xl p-6 sticky top-8">
              <h3 className="font-heading text-lg font-bold text-white mb-4">Détails du devis</h3>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-fgs-text-muted text-xs">Nom</Label>
                  <p className="text-white">{selectedQuote.name}</p>
                </div>

                {selectedQuote.company && (
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-fgs-text-muted" />
                    <p className="text-white">{selectedQuote.company}</p>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-fgs-text-muted" />
                  <a href={`mailto:${selectedQuote.email}`} className="text-fgs-primary hover:underline">
                    {selectedQuote.email}
                  </a>
                </div>

                {selectedQuote.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-fgs-text-muted" />
                    <a href={`tel:${selectedQuote.phone}`} className="text-fgs-primary hover:underline">
                      {selectedQuote.phone}
                    </a>
                  </div>
                )}

                {(selectedQuote.category || selectedQuote.project_type) && (
                  <div>
                    <Label className="text-fgs-text-muted text-xs">Type de projet</Label>
                    <p className="text-white">{getProjectType(selectedQuote.category || selectedQuote.project_type)}</p>
                  </div>
                )}

                {selectedQuote.message && (
                  <div>
                    <Label className="text-fgs-text-muted text-xs">Message</Label>
                    <p className="text-white text-sm bg-fgs-bg p-3 rounded-lg mt-1">
                      {selectedQuote.message}
                    </p>
                  </div>
                )}

                <div>
                  <Label className="text-fgs-text-muted text-xs">Date</Label>
                  <p className="text-white text-sm">
                    {new Date(selectedQuote.created_at).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>

                <div>
                  <Label className="text-fgs-text-muted text-xs">Statut</Label>
                  <select
                    value={selectedQuote.status || 'new'}
                    onChange={(e) => updateQuoteStatus(selectedQuote.quote_id, e.target.value)}
                    className="w-full mt-1 px-3 py-2 bg-fgs-bg border border-fgs-border rounded-lg text-white"
                  >
                    <option value="new">Nouveau</option>
                    <option value="pending">En attente</option>
                    <option value="contacted">Contacté</option>
                    <option value="quoted">Devis envoyé</option>
                    <option value="closed">Clôturé</option>
                  </select>
                </div>

                <div className="pt-4 flex gap-2">
                  <Button
                    onClick={() => window.open(`mailto:${selectedQuote.email}`, '_blank')}
                    className="flex-1 bg-fgs-primary hover:bg-fgs-primary-hover"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Contacter
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-fgs-surface border border-fgs-border rounded-xl p-12 text-center">
              <FileText className="w-12 h-12 text-fgs-text-muted mx-auto mb-4" />
              <p className="text-fgs-text-muted">Sélectionnez un devis</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminQuotes;
